// iter164 — Admin "Opening Balance Check": READ-ONLY comparison of stored
// non-anchor openings vs the new anchor-based cascade, for every school.
// Makes only GET requests (no writes, no activity logs) except the explicit
// useNewFundCascade switch the admin toggles.
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Play } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { dailyApi, fundApi } from "@/lib/setupApi";
import { cascadeMonth, consumedFromEntries, pickAnchor } from "@/lib/fundCascadeNew";
import { autoFillMdcf } from "@/lib/mdcf";
import { dryRationReportData, splitConsumed } from "@/lib/consumption";
import { ensureRateCards } from "@/lib/useRateCardsSync";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLS = ["balVatika", "primary", "upperPrimary", "highSchool"];
const pad = (n) => String(n).padStart(2, "0");
const isZero = (o) => CLS.every((c) => !Number(o?.[c] || 0));
const exists = (p) => p.then((d) => !!d && Object.keys(d).length > 0).catch(() => false);

// Tags the failing endpoint so the page can show exactly what broke.
const step = (label, p) => p.catch((e) => {
  const err = new Error(`${label} failed${e?.response?.status ? ` (HTTP ${e.response.status})` : ""}: ${e?.response?.data?.detail || e?.message || "network error"}`);
  throw err;
});
// Stored value looks like a cut-off copy of the calculated one (e.g. 19174 vs 19174.56, 0.12 vs 0.125).
const looksTruncated = (stored, calc) => stored !== 0 && stored !== calc && Math.abs(calc - stored) < 1
  && String(calc).startsWith(String(stored));
const hasExtraDecimals = (v, dec) => Math.abs(v * 10 ** dec - Math.round(v * 10 ** dec)) > 1e-6;

async function runCheck(onProgress) {
  await ensureRateCards();
  const users = (await step("GET /api/admin/users", axios.get(`${API}/admin/users`))).data || [];
  const schools = users.filter((u) => u.role === "school" && u.schoolId);
  const diffs = []; const ambiguous = []; const drReport = []; const truncated = []; const ucChanges = [];
  let entryCount = 0;
  for (let i = 0; i < schools.length; i += 20) {
    onProgress(`${Math.min(i + 20, schools.length)} / ${schools.length} schools`);
    const batch = schools.slice(i, i + 20);
    // eslint-disable-next-line no-await-in-loop
    const [lists, entryLists] = await Promise.all([
      Promise.all(batch.map((s) => step(`GET /api/fund-balances?schoolId=${s.schoolId}`, fundApi.list(s.schoolId)))),
      Promise.all(batch.map((s) => step(`GET /api/daily-entries?schoolId=${s.schoolId}`, dailyApi.list({ schoolId: s.schoolId })))),
    ]);
    for (let j = 0; j < lists.length; j += 1) {
      const s = batch[j]; const name = s.schoolName || s.udiseCode || s.schoolId;
      const byMonth = new Map();
      (entryLists[j] || []).forEach((e) => {
        const k = `${e.schoolId}|${e.date.slice(0, 7)}`;
        if (!byMonth.has(k)) byMonth.set(k, []);
        byMonth.get(k).push(e);
      });
      entryCount += (entryLists[j] || []).length;
      // Quarterly UC: today (MDM only, shared-group rice) vs this deploy (MDM+DR) vs per-class rice ON.
      const byQuarter = new Map();
      byMonth.forEach((ents, key) => {
        const [yy, mm] = key.split("|")[1].split("-").map(Number);
        const fy = mm >= 4 ? yy : yy - 1;
        const qk = `FY ${fy}-${String((fy + 1) % 100).padStart(2, "0")} Q${mm >= 4 ? Math.ceil((mm - 3) / 3) : 4}`;
        byQuarter.set(qk, [...(byQuarter.get(qk) || []), ...ents]);
      });
      byQuarter.forEach((ents, qk) => {
        const grp = splitConsumed(ents, { groupRice: true }); const own = splitConsumed(ents);
        ["balVatika", "primary", "upperPrimary"].forEach((c) => {
          const row = { school: name, quarter: qk, cls: c, drDays: grp.drDays.length,
            expBefore: grp.mdm.cost[c].toFixed(2), expAfter: grp.total.cost[c].toFixed(2),
            riceBefore: grp.mdm.rice[c].toFixed(3), riceAfterOff: grp.total.rice[c].toFixed(3), riceAfterOn: own.total.rice[c].toFixed(3) };
          if (row.expBefore !== row.expAfter || row.riceBefore !== row.riceAfterOff || row.riceAfterOff !== row.riceAfterOn) ucChanges.push(row);
        });
      });
      // Dry Ration Report: today's rule (group rice) vs per-class rice (switch ON).
      byMonth.forEach((ents, key) => {
        const now = dryRationReportData(ents, { drGroupRice: true });
        if (!now.days.length) return;
        const after = dryRationReportData(ents, { drGroupRice: false });
        CLS.forEach((c) => {
          const rd = after.totals[c].rice - now.totals[c].rice; const cd = after.totals[c].cost - now.totals[c].cost;
          if (Math.abs(rd) < 0.0005 && Math.abs(cd) < 0.005) return;
          drReport.push({ school: name, month: key.split("|")[1], cls: c, drDays: now.days.length,
            riceNow: now.totals[c].rice.toFixed(3), riceAfter: after.totals[c].rice.toFixed(3), riceDiff: rd.toFixed(3),
            costNow: now.totals[c].cost.toFixed(2), costAfter: after.totals[c].cost.toFixed(2), costDiff: cd.toFixed(2) });
        });
      });
      const recs = [...(lists[j] || [])].sort((a, b) => a.year - b.year || a.month - b.month);
      if (!recs.length) continue;
      recs.forEach((r) => CLS.forEach((c) => {
        const f = Number(r.openingFund?.[c] || 0); const k = Number(r.openingRice?.[c] || 0);
        if (hasExtraDecimals(f, 2)) truncated.push({ school: name, month: `${r.year}-${pad(r.month)}`, cls: c, kind: "Fund Rs", stored: f, calc: "", reason: "more than 2 decimals saved" });
        if (hasExtraDecimals(k, 3)) truncated.push({ school: name, month: `${r.year}-${pad(r.month)}`, cls: c, kind: "Rice kg", stored: k, calc: "", reason: "more than 3 decimals saved" });
      }));
      const anchor = pickAnchor(recs);
      const first = recs[0];
      const viaReceipt = (first.riceReceipts || []).length > 0 || (first.stockAdjustments || []).length > 0;
      if (!anchor || anchor !== first || (isZero(first.openingFund) && isZero(first.openingRice)) || (viaReceipt && isZero(first.openingFund))) {
        ambiguous.push({ school: name, earliest: `${first.year}-${pad(first.month)}`, anchor: anchor ? `${anchor.year}-${pad(anchor.month)}` : "none",
          zeroOpening: isZero(first.openingFund) && isZero(first.openingRice), createdByReceipt: viaReceipt || !!first.autoCreated });
      }
      if (!anchor) continue;
      const cache = new Map([["__records", recs]]);
      const last = recs[recs.length - 1];
      // DR expenditure already included before each month = what DR adds to that month's opening.
      const drBefore = new Map(); const cum = { fund: {}, rice: {} };
      CLS.forEach((c) => { cum.fund[c] = 0; cum.rice[c] = 0; });
      for (let y = anchor.year, m = anchor.month; y < last.year || (y === last.year && m <= last.month); m === 12 ? (y += 1, m = 1) : (m += 1)) {
        const cons = consumedFromEntries(byMonth.get(`${s.schoolId}|${y}-${pad(m)}`) || []);
        cache.set(`__cons:${y}-${m}`, cons);
        drBefore.set(`${y}-${m}`, { fund: { ...cum.fund }, rice: { ...cum.rice } });
        CLS.forEach((c) => { cum.fund[c] += cons.dr.cost[c]; cum.rice[c] += cons.dr.rice[c]; });
      }
      for (const r of recs) {
        if (r === anchor || r.year < anchor.year || (r.year === anchor.year && r.month < anchor.month)) continue;
        // eslint-disable-next-line no-await-in-loop
        const row = await cascadeMonth(s.schoolId, r.year, r.month, cache);
        const d = [];
        CLS.forEach((c) => {
          const f = Number((row.opening.fund[c] - Number(r.openingFund?.[c] || 0)).toFixed(2));
          const k = Number((row.opening.rice[c] - Number(r.openingRice?.[c] || 0)).toFixed(3));
          const sf = Number(r.openingFund?.[c] || 0); const cf = Number(row.opening.fund[c].toFixed(2));
          const sk = Number(r.openingRice?.[c] || 0); const ck = Number(row.opening.rice[c].toFixed(3));
          const drb = drBefore.get(`${r.year}-${r.month}`);
          if (Math.abs(f) > 0.01) d.push({ kind: "Fund Rs", cls: c, stored: sf, calc: row.opening.fund[c], diff: f, drExpenditureAdded: (drb?.fund[c] || 0).toFixed(2) });
          if (Math.abs(k) > 0.001) d.push({ kind: "Rice kg", cls: c, stored: sk, calc: row.opening.rice[c], diff: k, drExpenditureAdded: (drb?.rice[c] || 0).toFixed(3) });
          const tag = { school: name, month: `${r.year}-${pad(r.month)}`, cls: c };
          if (looksTruncated(sf, cf)) truncated.push({ ...tag, kind: "Fund Rs", stored: sf, calc: cf, reason: "saved value is a cut-off copy of the calculated one" });
          if (looksTruncated(sk, ck)) truncated.push({ ...tag, kind: "Rice kg", stored: sk, calc: ck, reason: "saved value is a cut-off copy of the calculated one" });
        });
        if (d.length) {
          const fyQ = r.month >= 4 ? Math.ceil((r.month - 3) / 3) : 4;
          const fyY = r.month >= 4 ? r.year : r.year - 1;
          // eslint-disable-next-line no-await-in-loop
          const [mdcf, uc] = await Promise.all([
            exists(axios.get(`${API}/mdcf/${s.schoolId}/${r.year}/${r.month}`).then((x) => x.data)),
            exists(axios.get(`${API}/generator-states/${s.schoolId}/${fyY}/${fyQ}`).then((x) => x.data)),
          ]);
          d.forEach((x) => diffs.push({ school: name, month: `${r.year}-${pad(r.month)}`, locked: !!r.isLocked, mdcf, uc, ...x }));
        }
      }
    }
  }
  return { diffs, ambiguous, drReport, truncated, ucChanges, schools: schools.length, entries: entryCount };
}

async function downloadXlsx(res) {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const add = (name, rows) => {
    const ws = wb.addWorksheet(name);
    if (!rows.length) { ws.addRow(["(none)"]); return; }
    ws.addRow(Object.keys(rows[0])); rows.forEach((r) => ws.addRow(Object.values(r)));
  };
  add("Opening differences", res.diffs); add("Ambiguous start", res.ambiguous); add("DR Report changes if ON", res.drReport); add("Quarterly UC changes", res.ucChanges); add("Possibly truncated", res.truncated);
  const blob = new Blob([await wb.xlsx.writeBuffer()], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "opening-balance-check.xlsx"; a.click();
}

const Table = ({ title, rows, testId }) => (
  <section className="mt-5 rounded-xl border border-border bg-white p-4" data-testid={testId}>
    <h2 className="mb-2 font-heading text-sm font-bold">{title} ({rows.length})</h2>
    {rows.length === 0 ? <p className="text-xs text-ink-700">None.</p> : (
      <div className="max-h-96 overflow-auto"><table className="w-full text-xs">
        <thead className="bg-sand-100 text-left"><tr>{Object.keys(rows[0]).map((k) => <th key={k} className="p-1.5">{k}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-border">{Object.values(r).map((v, j) => <td key={j} className="p-1.5 font-mono">{String(v)}</td>)}</tr>)}</tbody>
      </table></div>
    )}
  </section>
);

const TEXT_PATHS = [
  ...["balBatika", "primaryClass", "upperPrimaryClass", "upperSchool"].flatMap((c) => [`monthly.section6.${c}.expense`, `monthly.section7.${c}.expense`]),
  ...["cookingCostBalVatika", "cookingCostPrimary", "cookingCostUpperPrimary"].map((k) => `eng.fundDetails.${k}.expenditureDuringMonth`),
  ...["balVatikaRice", "primaryRice", "upperPrimaryRice"].map((k) => `eng.grainDetails.${k}.consumptionDuringMonth`),
  ...["totalWorkingDays", "mdmServedDays", "totalFoodExpense"].flatMap((f) => ["balBatika", "primaryClass", "upperPrimaryClass", "upperSchool"].map((c) => `monthly.section2.${f}.${c}`)),
  ...["schoolDays", "mdmDaysServed", "totalMealsServed"].flatMap((f) => ["balVatika", "primary", "upperPrimary"].map((c) => `eng.mealsAvailed.${f}.${c}`)),
];
const dig = (o, path) => path.split(".").reduce((x, k) => (x == null ? x : x[k]), o);

// Re-computes only the expense-text fields of one saved MDCF with the fixed formatter.
async function regenerateMdcf(row) {
  await ensureRateCards();
  const doc = (await step("GET /api/mdcf", axios.get(`${API}/mdcf/${row.schoolId}/${row.year}/${row.month}`))).data;
  const last = new Date(row.year, row.month, 0).getDate();
  const ents = await step("GET /api/daily-entries", dailyApi.list({ schoolId: row.schoolId, fromDate: `${row.year}-${pad(row.month)}-01`, toDate: `${row.year}-${pad(row.month)}-${last}` }));
  const fresh = autoFillMdcf({ base: doc.data, school: null, dailyEntries: ents, year: row.year, month: row.month });
  // Only cells whose text actually changes are written.
  const fields = Object.fromEntries(TEXT_PATHS.map((p) => [p, String(dig(fresh, p) ?? "")]).filter(([p, v]) => String(dig(doc.data, p) ?? "") !== v));
  if (!Object.keys(fields).length) return { changed: 0 };
  return (await step("POST /api/admin/mdcf-regenerate", axios.post(`${API}/admin/mdcf-regenerate`, { id: row.id, fields }))).data;
}

function MdcfImpact() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState("");
  const load = () => axios.get(`${API}/admin/mdcf-impact`).then((r) => setData(r.data)).catch((e) => toast.error(`GET /api/admin/mdcf-impact failed: ${e?.response?.status || e.message}`));
  const regen = async (row) => {
    if (!window.confirm(`Regenerate the expense text of ${row.schoolName} ${row.year}-${pad(row.month)}? Only the day/meal counts, sections 6/7 expense and the English bill expenditure/consumption cells change. This is logged.`)) return;
    setBusyId(row.id);
    try { const r = await regenerateMdcf(row); toast.success(`Regenerated · ${r.changed} cells`); await load(); } catch (e) { toast.error(e.message); }
    finally { setBusyId(""); }
  };
  return (
    <section className="mt-5 rounded-xl border border-border bg-white p-4" data-testid="mdcf-impact">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-sm font-bold">Saved MDCFs with the "+2.00 / +3.00" text bug</h2>
        <Button variant="outline" size="sm" onClick={load} data-testid="mdcf-impact-load">Check saved MDCFs</Button>
      </div>
      {data && (
        <>
          <p className="mt-2 text-xs text-ink-700" data-testid="mdcf-impact-summary">{data.bad.length} affected saved MDCF(s) · {data.saves.length} MDCF save(s) since {data.since} (no download events are recorded)</p>
          <div className="mt-2 max-h-96 overflow-auto"><table className="w-full text-xs">
            <thead className="bg-sand-100 text-left"><tr>{["School", "UDISE", "Month", "Updated", "Bad cells", "Sample", ""].map((h) => <th key={h} className="p-1.5">{h}</th>)}</tr></thead>
            <tbody>{data.bad.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`mdcf-bad-${r.id}`}>
                <td className="p-1.5">{r.schoolName}</td><td className="p-1.5 font-mono">{r.udiseCode}</td>
                <td className="p-1.5 font-mono">{r.year}-{pad(r.month)}</td><td className="p-1.5 font-mono">{String(r.updatedAt || "").slice(0, 10)}</td>
                <td className="p-1.5 font-mono">{r.badFields}</td><td className="p-1.5 font-mono">{r.sample}</td>
                <td className="p-1.5"><Button size="sm" disabled={busyId === r.id} onClick={() => regen(r)} data-testid={`mdcf-regen-${r.id}`}>{busyId === r.id ? "…" : "Regenerate"}</Button></td>
              </tr>
            ))}</tbody>
          </table></div>
          <Table title="MDCF saves since the bug date (schools to ask to reprint)" rows={data.saves.map((x) => ({ school: x.schoolName, savedAt: x.createdAt }))} testId="mdcf-impact-saves" />
        </>
      )}
    </section>
  );
}

export default function AdminFundCheck() {
  const [flag, setFlag] = useState(false);
  const [riceFlag, setRiceFlag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { axios.get(`${API}/app-settings/fund-cascade`).then((r) => { setFlag(!!r.data.useNewFundCascade); setRiceFlag(!!r.data.useClasswiseRiceNorms); }).catch(() => {}); }, []);
  const toggleRice = async (v) => {
    try {
      const r = await axios.post(`${API}/app-settings/fund-cascade`, { useClasswiseRiceNorms: v });
      setRiceFlag(r.data.useClasswiseRiceNorms);
      const { setUseClasswiseRiceNorms } = await import("@/lib/riceNorms");
      setUseClasswiseRiceNorms(r.data.useClasswiseRiceNorms);
      toast.success(`Class-wise rice norms ${v ? "ON" : "OFF"}`);
    } catch { toast.error("Could not change setting"); }
  };
  const toggle = async (v) => {
    try {
      const r = await axios.post(`${API}/app-settings/fund-cascade`, { useNewFundCascade: v });
      setFlag(r.data.useNewFundCascade);
      const { setUseNewFundCascade } = await import("@/lib/fundCascade");
      setUseNewFundCascade(r.data.useNewFundCascade);
      toast.success(`New fund calculation ${v ? "ON" : "OFF"}`);
    } catch { toast.error("Could not change setting"); }
  };
  const run = async () => {
    setBusy(true); setRes(null); setError("");
    try { setRes(await runCheck(setProgress)); } catch (e) { setError(e.message); toast.error("Check failed — see details on the page"); }
    finally { setBusy(false); setProgress(""); }
  };
  return (
    <main className="min-h-screen bg-sand-50 pb-24 text-ink-900">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-6">
        <h1 className="font-heading text-2xl font-bold" data-testid="fund-check-title">Opening Balance Check</h1>
        <p className="mt-1 text-xs text-ink-700">Read-only. Compares saved openings of later months with the new calculated opening (anchor → previous closing). Nothing is written.</p>
        <div className="mt-4 flex flex-wrap items-center gap-4 rounded-xl border border-border bg-white p-3">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <Switch checked={flag} onCheckedChange={toggle} data-testid="fund-cascade-switch" /> Use new fund calculation (useNewFundCascade)
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <Switch checked={riceFlag} onCheckedChange={toggleRice} data-testid="rice-norms-switch" /> Class-wise rice norms in UC + DR Report (useClasswiseRiceNorms)
          </label>
          <Button onClick={run} disabled={busy} data-testid="fund-check-run"><Play className="mr-1 h-4 w-4" />{busy ? progress || "Running…" : "Run check"}</Button>
          {res && <Button variant="outline" onClick={() => downloadXlsx(res)} data-testid="fund-check-xlsx"><Download className="mr-1 h-4 w-4" />Download .xlsx</Button>}
        </div>
        {error && <p className="mt-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs font-semibold text-destructive" data-testid="fund-check-error">{error}</p>}
        {res && <p className="mt-3 text-xs text-ink-700" data-testid="fund-check-summary">{res.schools} schools · {res.entries} daily entries · {res.diffs.length} differing values · {res.ambiguous.length} ambiguous · {res.truncated.length} possibly truncated</p>}
        {res && <Table title="Later-month opening differences" rows={res.diffs} testId="fund-check-diffs" />}
        {res && <Table title="Ambiguous starting months" rows={res.ambiguous} testId="fund-check-ambiguous" />}
        {res && <Table title="Saved openings that look truncated" rows={res.truncated} testId="fund-check-truncated" />}
        {res && <Table title="Dry Ration Report figures that change when useClasswiseRiceNorms is ON (per-class rice)" rows={res.drReport} testId="fund-check-dr" />}
        {res && <Table title="Quarterly UC: before (today, MDM only) → after this deploy (MDM + DR) → with useClasswiseRiceNorms ON" rows={res.ucChanges} testId="fund-check-uc" />}
        <MdcfImpact />
      </div>
    </main>
  );
}
